import React, { useCallback, useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Save, Settings as Cog } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const DEFAULTS = {
  default_label_tier: "internal",
  default_dlp_action: "log",
  classifier_min_confidence: 0.7,
  audit_retention_days: 365,
  login_lockout_attempts: 5,
  login_lockout_minutes: 15,
  session_minutes: 480,
  ddos_auto_blackhole_default: true,
  web_proxy_default_action: "allow",
  require_2fa_for_admins: false,
  default_bandwidth_kbps_per_user: 0,
  default_bandwidth_kbps_per_route: 0,
  safe_search_enforced: false,
  pcap_max_duration_seconds: 300,
  pcap_max_bytes: 200 * 1024 * 1024,
  ephemeral_session_max_minutes: 60,
};

export default function Settings() {
  const { user } = useAuth();
  const [form, setForm] = useState(DEFAULTS);
  const [busy, setBusy] = useState(false);
  const isAdmin = user?.role === "admin";

  const load = useCallback(async () => {
    try { const { data } = await api.get("/settings"); setForm({ ...DEFAULTS, ...data }); }
    catch (err) { console.error("settings load failed", err); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try { await api.put("/settings", form); toast.success("Settings saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const set = (k, v) => setForm({ ...form, [k]: v });
  const num = (k) => (e) => set(k, parseInt(e.target.value || "0", 10));
  const fnum = (k) => (e) => set(k, parseFloat(e.target.value || "0"));

  return (
    <Layout title="Runtime Settings" subtitle="ADMIN">
      <div className="max-w-3xl space-y-6">
        <div className="border border-zinc-800 rounded-sm bg-zinc-900/60 p-5">
          <SectionHeader title="Data classification defaults" />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Default label tier">
              <SelectField value={form.default_label_tier} onChange={(v) => set("default_label_tier", v)}
                options={["public", "internal", "confidential", "restricted"]} testid="set-label-tier" />
            </Field>
            <Field label="Default DLP action">
              <SelectField value={form.default_dlp_action} onChange={(v) => set("default_dlp_action", v)}
                options={["log", "alert", "block"]} testid="set-dlp-action" />
            </Field>
            <Field label="Classifier min confidence (0–1)">
              <Input data-testid="set-classifier-conf" type="number" step="0.05" min="0" max="1"
                value={form.classifier_min_confidence} onChange={fnum("classifier_min_confidence")}
                className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" />
            </Field>
            <Field label="Web proxy default action">
              <SelectField value={form.web_proxy_default_action} onChange={(v) => set("web_proxy_default_action", v)}
                options={["allow", "deny"]} testid="set-proxy-action" />
            </Field>
          </div>
        </div>

        <div className="border border-zinc-800 rounded-sm bg-zinc-900/60 p-5">
          <SectionHeader title="Authentication" />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Login lockout — attempts">
              <Input data-testid="set-lockout-attempts" type="number" value={form.login_lockout_attempts}
                onChange={num("login_lockout_attempts")} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" />
            </Field>
            <Field label="Login lockout — minutes">
              <Input data-testid="set-lockout-minutes" type="number" value={form.login_lockout_minutes}
                onChange={num("login_lockout_minutes")} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" />
            </Field>
            <Field label="Session length (minutes)">
              <Input data-testid="set-session-minutes" type="number" value={form.session_minutes}
                onChange={num("session_minutes")} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" />
            </Field>
            <ToggleField label="Require 2FA for admins" checked={form.require_2fa_for_admins}
              onChange={(v) => set("require_2fa_for_admins", v)} testid="set-2fa-admins" />
          </div>
        </div>

        <div className="border border-zinc-800 rounded-sm bg-zinc-900/60 p-5">
          <SectionHeader title="Operations" />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Audit retention (days)">
              <Input data-testid="set-audit-retention" type="number" value={form.audit_retention_days}
                onChange={num("audit_retention_days")} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" />
            </Field>
            <ToggleField label="DDoS auto-blackhole default" checked={form.ddos_auto_blackhole_default}
              onChange={(v) => set("ddos_auto_blackhole_default", v)} testid="set-ddos-blackhole" />
            <Field label="Ephemeral elevation max (minutes)">
              <Input data-testid="set-eph-max" type="number" value={form.ephemeral_session_max_minutes}
                onChange={num("ephemeral_session_max_minutes")} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" />
            </Field>
          </div>
        </div>

        <div className="border border-zinc-800 rounded-sm bg-zinc-900/60 p-5">
          <SectionHeader title="QoS defaults (0 = unlimited)" />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Per-user default (kbps)">
              <Input data-testid="set-bw-user" type="number" value={form.default_bandwidth_kbps_per_user}
                onChange={num("default_bandwidth_kbps_per_user")} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" />
            </Field>
            <Field label="Per-route default (kbps)">
              <Input data-testid="set-bw-route" type="number" value={form.default_bandwidth_kbps_per_route}
                onChange={num("default_bandwidth_kbps_per_route")} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" />
            </Field>
          </div>
        </div>

        <div className="border border-zinc-800 rounded-sm bg-zinc-900/60 p-5">
          <SectionHeader title="Web safeguards" />
          <div className="grid grid-cols-2 gap-4">
            <ToggleField label="Force SafeSearch on outbound queries" checked={form.safe_search_enforced}
              onChange={(v) => set("safe_search_enforced", v)} testid="set-safe-search" />
          </div>
        </div>

        <div className="border border-zinc-800 rounded-sm bg-zinc-900/60 p-5">
          <SectionHeader title="Packet capture caps" />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Max duration (seconds)">
              <Input data-testid="set-pcap-dur" type="number" value={form.pcap_max_duration_seconds}
                onChange={num("pcap_max_duration_seconds")} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" />
            </Field>
            <Field label="Max bytes per job">
              <Input data-testid="set-pcap-bytes" type="number" value={form.pcap_max_bytes}
                onChange={num("pcap_max_bytes")} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" />
            </Field>
          </div>
        </div>

        <div className="flex items-center justify-between pt-2">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            <Cog className="w-3 h-3 inline mr-1" /> RUNTIME · STORED IN runtime_settings
          </div>
          <Button data-testid="save-settings-btn" onClick={save} disabled={busy || !isAdmin}
            className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm gap-2">
            <Save className="w-4 h-4" /> {busy ? "SAVING..." : "SAVE"}
          </Button>
        </div>
        {!isAdmin && <div className="text-xs text-amber-400 font-mono">View-only — admin role required to save.</div>}
      </div>
    </Layout>
  );
}

function SectionHeader({ title }) {
  return <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-400 mb-4">// {title}</div>;
}
function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">{label}</Label>
      {children}
    </div>
  );
}
function ToggleField({ label, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between border border-zinc-800 rounded-sm px-3 py-2 bg-zinc-900">
      <span className="text-sm">{label}</span>
      <Switch data-testid={testid} checked={checked} onCheckedChange={onChange} />
    </div>
  );
}
function SelectField({ value, onChange, options, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono uppercase text-xs">
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="bg-zinc-950 border-zinc-800">
        {options.map((o) => <SelectItem key={o} value={o} className="font-mono uppercase text-xs">{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
